import React from 'react';
import { useDragLayer } from 'react-dnd';
import TibbitsData from '@appblocks/platforms/TibbitDefinitions/tibbits.json';
import Tibbit_module_narrow from '@appblocks/frontend/client/Components/TPP/Tibbit_module_narrow';
import Tibbit_module_wide from '@appblocks/frontend/client/Components/TPP/Tibbit_module_wide';
import Tibbit_hybrid_narrow from '@appblocks/frontend/client/Components/TPP/Tibbit_hybrid_narrow';
import Tibbit_hybrid_wide from '@appblocks/frontend/client/Components/TPP/Tibbit_hybrid_wide';
import Tibbit_connector_narrow from '@appblocks/frontend/client/Components/TPP/Tibbit_connector_narrow';
import Tibbit_connector_wide from '@appblocks/frontend/client/Components/TPP/Tibbit_connector_wide';
import Tibbit_wifi from '@appblocks/frontend/client/Components/TPP/Tibbit_wifi';
import { useCustomShapes } from '@appblocks/frontend/client/Components/PeripheralsWiringEditor/CustomShapeContext';
import {
    FaSquare, FaCircle, FaCaretUp, FaMapPin, FaFont, FaLayerGroup,
} from 'react-icons/fa';

// Map form factors to SVG components
const tibbitSVGs = {
    M1: Tibbit_module_narrow,
    M2: Tibbit_module_wide,
    H1: Tibbit_hybrid_narrow,
    H2: Tibbit_hybrid_wide,
    C1: Tibbit_connector_narrow,
    C2: Tibbit_connector_wide,
    WIFI: Tibbit_wifi,
};

// Build form factor lookup from tibbits data
const tibbitFormFactors = {};
for (let i = 0; i < TibbitsData.length; i += 1) {
    const tibbit = TibbitsData[i];
    tibbitFormFactors[tibbit.good.model] = {
        form: tibbit.main.form.replace(/[ST]/, ''),
        formOriginal: tibbit.main.form,
        category: tibbit.main.category,
        desc_svg: tibbit.good.desc_svg,
        mates: tibbit.main.mates,
        power: tibbit.main.power,
        requires: tibbit.main.requires,
        lines: tibbit.main.lines,
    };
}

// Shape definitions for preview rendering
const SHAPE_CONFIGS = {
    rectangle: {
        name: 'Rectangle',
        icon: FaSquare,
        fill: '#e3f2fd',
        stroke: '#1976d2',
        width: 60,
        height: 40,
    },
    circle: {
        name: 'Circle',
        icon: FaCircle,
        fill: '#fff3e0',
        stroke: '#f57c00',
        width: 50,
        height: 50,
    },
    triangle: {
        name: 'Triangle',
        icon: FaCaretUp,
        fill: '#e8f5e9',
        stroke: '#388e3c',
        width: 50,
        height: 50,
    },
    point: {
        name: 'Point',
        icon: FaMapPin,
        fill: '#3498db',
        stroke: '#2980b9',
        width: 24,
        height: 24,
    },
    label: {
        name: 'Label',
        icon: FaFont,
        fill: '#333333',
        stroke: 'none',
        width: 60,
        height: 24,
    },
};

// Layer styles for the drag preview
const layerStyles = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 10000,
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
};

/**
 * Get the transform style for the drag preview based on cursor position
 */
function getItemStyles(currentOffset, previewWidth, previewHeight) {
    if (!currentOffset) {
        return {
            display: 'none',
        };
    }

    // Center the preview under the cursor
    const { x, y } = currentOffset;
    const offsetX = x - previewWidth / 2;
    const offsetY = y - previewHeight / 2;
    const transform = `translate(${offsetX}px, ${offsetY}px)`;

    return {
        transform,
        WebkitTransform: transform,
        width: previewWidth,
        height: previewHeight,
    };
}

/**
 * Render tibbit SVG preview
 */
function renderTibbitPreview(tibbitId) {
    const tibbitInfo = tibbitFormFactors[tibbitId];
    if (!tibbitInfo) {
        return null;
    }

    const TibbitSvg = tibbitSVGs[tibbitInfo.form];
    if (!TibbitSvg) {
        return null;
    }

    // Create an instance to get dimensions
    const tibbitInstance = new TibbitSvg({});
    const tibbitWidth = tibbitInstance.width || 100;
    const tibbitHeight = tibbitInstance.height || 50;

    // Scale factor for the preview
    const scale = 2;
    const previewWidth = tibbitWidth * scale;
    const previewHeight = tibbitHeight * scale;

    // Determine transform based on form factor
    let transform = '';
    switch (tibbitInfo.form) {
        case 'C1':
            transform = 'translate(48, 0) rotate(90)';
            break;
        case 'C2':
            transform = 'translate(100, 0) rotate(90)';
            break;
        case 'H1':
            transform = 'translate(50, 0)';
            break;
        case 'H2':
            transform = 'translate(50, 0)';
            break;
        case 'WIFI':
            transform = 'translate(120, 0) rotate(90)';
            break;
        default:
            transform = '';
            break;
    }

    return {
        width: previewWidth,
        height: previewHeight,
        element: (
            <svg
                width={previewWidth}
                height={previewHeight}
                viewBox={`0 0 ${tibbitWidth} ${tibbitHeight}`}
                style={{ backgroundColor: 'transparent' }}
            >
                <g transform={transform}>
                    <TibbitSvg
                        num={`#${tibbitId}`}
                        category={tibbitInfo.category}
                        lines={tibbitInfo.lines}
                        descriptions={tibbitInfo.desc_svg}
                    />
                </g>
            </svg>
        ),
    };
}

/**
 * Render shape SVG preview
 */
function renderShapePreview(shapeType) {
    const config = SHAPE_CONFIGS[shapeType];
    if (!config) {
        return null;
    }

    const { width, height, fill, stroke } = config;
    const scale = 1.5;
    const previewWidth = width * scale;
    const previewHeight = height * scale;
    const padding = 4;

    let shapeElement;
    switch (shapeType) {
        case 'rectangle':
            shapeElement = (
                <rect
                    x={padding}
                    y={padding}
                    width={width - padding * 2}
                    height={height - padding * 2}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={2}
                    rx={4}
                />
            );
            break;
        case 'circle':
            shapeElement = (
                <ellipse
                    cx={width / 2}
                    cy={height / 2}
                    rx={(width - padding * 2) / 2}
                    ry={(height - padding * 2) / 2}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={2}
                />
            );
            break;
        case 'triangle': {
            const centerX = width / 2;
            const points = `${centerX},${padding} ${padding},${height - padding} ${width - padding},${height - padding}`;
            shapeElement = (
                <polygon
                    points={points}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={2}
                />
            );
            break;
        }
        case 'point':
            shapeElement = (
                <>
                    {/* Outer ring */}
                    <circle
                        cx={width / 2}
                        cy={height / 2}
                        r={10}
                        fill="white"
                        stroke={fill}
                        strokeWidth={2}
                    />
                    {/* Inner dot */}
                    <circle
                        cx={width / 2}
                        cy={height / 2}
                        r={4}
                        fill={fill}
                    />
                </>
            );
            break;
        case 'label':
            shapeElement = (
                <text
                    x={width / 2}
                    y={height / 2}
                    fontSize={14}
                    fill={fill}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontWeight="500"
                >
                    Label
                </text>
            );
            break;
        default:
            return null;
    }

    return {
        width: previewWidth,
        height: previewHeight,
        element: (
            <svg
                width={previewWidth}
                height={previewHeight}
                viewBox={`0 0 ${width} ${height}`}
                style={{ backgroundColor: 'transparent' }}
            >
                {shapeElement}
            </svg>
        ),
    };
}

/**
 * Apply default property values to SVG for custom shape preview
 */
function applyDefaultPropertiesToSvg(svgString, properties) {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgString, 'image/svg+xml');

        const parseError = doc.querySelector('parsererror');
        if (parseError) {
            return svgString;
        }

        properties.forEach((prop) => {
            if (prop.defaultValue && prop.targetSelector) {
                const elements = doc.querySelectorAll(prop.targetSelector);
                elements.forEach((el) => {
                    if (prop.targetAttribute === 'textContent') {
                        el.textContent = prop.defaultValue;
                    } else {
                        el.setAttribute(prop.targetAttribute, prop.defaultValue);
                    }
                });
            }
        });

        return new XMLSerializer().serializeToString(doc);
    } catch (e) {
        return svgString;
    }
}

/**
 * Render custom shape SVG preview
 */
function renderCustomShapePreview(customShapeDefinition) {
    if (!customShapeDefinition || !customShapeDefinition.svgData) {
        return null;
    }

    try {
        // Apply default property values to the SVG
        const svgWithDefaults = applyDefaultPropertiesToSvg(
            customShapeDefinition.svgData,
            customShapeDefinition.properties || [],
        );

        const parser = new DOMParser();
        const doc = parser.parseFromString(svgWithDefaults, 'image/svg+xml');
        const svg = doc.querySelector('svg');

        if (!svg) {
            return null;
        }

        // Get original dimensions from SVG
        let originalWidth = parseFloat(svg.getAttribute('width')) || 100;
        let originalHeight = parseFloat(svg.getAttribute('height')) || 100;

        // Try to get dimensions from viewBox if width/height not set
        const viewBox = svg.getAttribute('viewBox');
        if (viewBox) {
            const [, , vbWidth, vbHeight] = viewBox.split(/\s+/).map(parseFloat);
            if (vbWidth && vbHeight) {
                originalWidth = vbWidth;
                originalHeight = vbHeight;
            }
        }

        // Scale the preview to a reasonable size (max 80px)
        const maxSize = 80;
        const scale = Math.min(maxSize / originalWidth, maxSize / originalHeight, 1.5);
        const previewWidth = originalWidth * scale;
        const previewHeight = originalHeight * scale;

        // Update SVG dimensions for preview
        svg.setAttribute('width', String(previewWidth));
        svg.setAttribute('height', String(previewHeight));
        if (!viewBox) {
            svg.setAttribute('viewBox', `0 0 ${originalWidth} ${originalHeight}`);
        }

        return {
            width: previewWidth,
            height: previewHeight,
            element: (
                <div
                    dangerouslySetInnerHTML={{
                        __html: new XMLSerializer().serializeToString(doc),
                    }}
                    style={{ width: previewWidth, height: previewHeight }}
                />
            ),
        };
    } catch (e) {
        return null;
    }
}

/**
 * Render custom group preview
 * Shows a styled box with group icon and element count
 */
function renderCustomGroupPreview(customGroupDefinition) {
    if (!customGroupDefinition) {
        return null;
    }

    const elementCount = customGroupDefinition.elements?.length || 0;
    const previewWidth = 100;
    const previewHeight = 60;

    return {
        width: previewWidth,
        height: previewHeight,
        element: (
            <div
                style={{
                    width: previewWidth,
                    height: previewHeight,
                    backgroundColor: '#e3f2fd',
                    border: '2px dashed #1976d2',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    boxSizing: 'border-box',
                }}
            >
                <FaLayerGroup
                    size={20}
                    style={{ color: '#1976d2', marginBottom: '4px' }}
                />
                <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#1976d2',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                }}
                >
                    {customGroupDefinition.name || 'Group'}
                </div>
                <div style={{
                    fontSize: '9px',
                    color: '#666',
                }}
                >
                    {elementCount}
                    {' '}
                    element
                    {elementCount !== 1 ? 's' : ''}
                </div>
            </div>
        ),
    };
}

/**
 * CustomDragLayer component renders SVG previews while dragging
 * Supports TIBBIT, SHAPE drag types (including custom shapes and custom groups)
 */
const CustomDragLayer = () => {
    // Get custom shape and group definitions from context
    const { customShapeDefinitions, customGroupDefinitions } = useCustomShapes();

    const {
        itemType,
        isDragging,
        item,
        currentOffset,
    } = useDragLayer((monitor) => ({
        itemType: monitor.getItemType(),
        isDragging: monitor.isDragging(),
        item: monitor.getItem(),
        currentOffset: monitor.getClientOffset(),
    }));

    if (!isDragging || !currentOffset) {
        return null;
    }

    let preview = null;

    // Handle TIBBIT drag type
    if (itemType === 'TIBBIT' && item?.tibbitId) {
        preview = renderTibbitPreview(item.tibbitId);
    }

    // Handle SHAPE drag type
    if (itemType === 'SHAPE' && item?.shapeType) {
        // Check if it's a custom group
        if (item.shapeType === 'customGroup' && item.customGroupId) {
            const customGroupDef = customGroupDefinitions.find(
                (def) => def.id === item.customGroupId,
            );
            if (customGroupDef) {
                preview = renderCustomGroupPreview(customGroupDef);
            }
        }
        // Check if it's a custom shape
        else if (item.shapeType === 'custom' && item.customShapeId) {
            const customShapeDef = customShapeDefinitions.find(
                (def) => def.id === item.customShapeId,
            );
            if (customShapeDef) {
                preview = renderCustomShapePreview(customShapeDef);
            }
        } else {
            // Regular shape preview
            preview = renderShapePreview(item.shapeType);
        }
    }

    if (!preview) {
        return null;
    }

    return (
        <div style={layerStyles}>
            <div style={getItemStyles(currentOffset, preview.width, preview.height)}>
                <div
                    style={{
                        opacity: 0.9,
                        filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                    }}
                >
                    {preview.element}
                </div>
            </div>
        </div>
    );
};

export default CustomDragLayer;
